import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { Tournament } from "model/Tournament/Tournament";
import { startAppListening } from "app/listenerMiddleware";
import { nonUndefined } from "Utility";
import { setupMatchSnapshot } from "db/info/matchInfo";

type TournamentTrackingDetails = {
  initialLoad: boolean;
  matchesTracked: boolean;
};

// todo - does this belong here?
export const isTournamentTracked = (
  trackingDetails: TournamentTrackingDetails | undefined
) => {
  return trackingDetails !== undefined;
};

export interface ITournamentSlice {
  intialLoadComplete: boolean;
  tournaments: { [key: string]: Tournament | undefined };
  trackingDetails: { [key: string]: TournamentTrackingDetails | undefined };
}

const initialState: ITournamentSlice = {
  intialLoadComplete: false,
  tournaments: {},
  trackingDetails: {},
};

export const tournamentSlice = createSlice({
  name: "tournament",
  initialState,
  reducers: {
    setMatchesTracked: (
      state,
      action: PayloadAction<{
        tournamentId: string;
      }>
    ) => {
      const { tournamentId } = action.payload;
      const trackingDetails = state.trackingDetails[tournamentId];
      if (!!trackingDetails) {
        trackingDetails.matchesTracked = true;
      }
    },
    trackTournaments: (
      state,
      action: PayloadAction<{
        initialLoad: boolean;
        tournamentIds: string[];
      }>
    ) => {
      const { initialLoad, tournamentIds } = action.payload;
      const newTournamentIdsToTrack = tournamentIds.filter(
        (id) => !isTournamentTracked(state.trackingDetails[id])
      );

      for (let i = 0; i < newTournamentIdsToTrack.length; i++) {
        const tournamentId = newTournamentIdsToTrack[i];
        state.trackingDetails[tournamentId] = {
          initialLoad,
          matchesTracked: false,
        };
      }
    },
    updateTournaments: (
      state,
      action: PayloadAction<{
        tournaments: Tournament[];
      }>
    ) => {
      const { tournaments } = action.payload;
      for (let i = 0; i < tournaments.length; i++) {
        const tournament = tournaments[i];
        state.tournaments[tournament.id] = tournament;
      }

      if (!state.intialLoadComplete) {
        state.intialLoadComplete = true;
      }
    },
  },
});

export const { setMatchesTracked, trackTournaments, updateTournaments } =
  tournamentSlice.actions;
export const tournamentReducer = tournamentSlice.reducer;

startAppListening({
  matcher: isAnyOf(trackTournaments, updateTournaments),
  effect: (action, listenerApi) => {
    if (!(trackTournaments.match(action) || updateTournaments.match(action))) {
      return;
    }

    const state = listenerApi.getState();
    const tournaments = nonUndefined(
      trackTournaments.match(action)
        ? action.payload.tournamentIds.map(
            (id) => state.tournament.tournaments[id]
          )
        : action.payload.tournaments
    );

    const trackedTournaments = tournaments.filter((t) =>
      isTournamentTracked(state.tournament.trackingDetails[t.id])
    );

    for (let i = 0; i < trackedTournaments.length; i++) {
      const tournamentId = trackedTournaments[i].id;
      const trackingDetails = state.tournament.trackingDetails[tournamentId];
      if (trackingDetails?.matchesTracked === false) {
        listenerApi.dispatch(setMatchesTracked({ tournamentId }));
        setupMatchSnapshot(listenerApi.dispatch, tournamentId);
      }
    }
  },
});
